export type Plan = (typeof plansData.plans)[number];
export type PlanInfo = Plan["plan_info"][number];
export type AdditionalUserPlan = Plan["additional_user_plan"];
export const plansData = {
  plans: [
    {
      plan_name: "1 Year",
      plan_id: 11,
      plan_info: [
        {
          id: 1,
          base_price: 360,
          discount_percent: 50,
          currency_symbol: "$",
        },
      ],
      label: "1 Year",
      additional_user_plan: {
        id: 2,
        base_price: 120,
        discount_percent: null,
        currency_symbol: "$",
      },
      offer_available: true,
      credit_card_fee_percent: "4",
      tax_percent: "6",
      base_included_users: 1,
    },
    {
      plan_name: "2 years",
      plan_id: 22,
      plan_info: [
        {
          label: "Year 1:",
          id: 3,
          base_price: 360,
          discount_percent: 50,
          currency_symbol: "$",
        },
        {
          label: "Year 2:",
          id: 4,
          base_price: 360,
          discount_percent: 25,
          currency_symbol: "$",
        },
      ],
      label: "2 years",
      additional_user_plan: {
        id: 5,
        base_price: 120,
        discount_percent: 5,
        currency_symbol: "$",
      },
      offer_available: true,
      credit_card_fee_percent: "4",
      tax_percent: "6",
      base_included_users: 1,
      tooltip: {
        label: "Locked users and pricing for 2 years",
        tooltip_text:
          "Additional users are billed for the 2-year plan. The selected number of users and pricing are locked for the full two-year term. Additional users can be purchased later, but promotional pricing may not apply.",
        enableTooltip: true,
      },
    },
  ],
};

export type AppliedCoupon = {
  coupon_code?: string;
  discount_type?: string;
  discount_value?: number;
  applicable_product_ids?: string[];
} | null;

export const computePlanPrice = (
  plan: Plan,
  additionalUsers: number = 0,
  isTaxApplicable: boolean = false,
  coupon: AppliedCoupon = null
) => {
  if (!plan) {
    return null;
  }
  const planBase = (plan.plan_info || []).reduce((sum, p: any) => {
    const base = p.base_price ?? 0;
    const discount = p.discount_percent ? Number(p.discount_percent) : 0;
    const discounted = base * (1 - discount / 100);
    return sum + discounted;
  }, 0);
  const addBase = plan.additional_user_plan?.base_price ?? 0;
  const addDiscountedPrice = plan.additional_user_plan?.discounted_price ?? 0;
  const addDiscount = plan.additional_user_plan?.discount_percent
    ? Number(plan.additional_user_plan.discount_percent)
    : 0;
  const addPerSeat =
    addDiscountedPrice !== 0
      ? addDiscountedPrice
      : addBase * (1 - addDiscount / 100);
  const addUsersCost =
    addPerSeat * (plan?.plan_type ?? 1) * Math.max(0, additionalUsers);
  const subTotal = planBase + addUsersCost;
  const planBaseWithoutDiscount = (plan.plan_info || []).reduce(
    (sum, p: any) => sum + (p.base_price ?? 0),
    0
  );
  const addUsersCostWithoutDiscount =
    addBase * (plan?.plan_type ?? 1) * Math.max(0, additionalUsers);
  const subTotalSavings =
    planBaseWithoutDiscount + addUsersCostWithoutDiscount - subTotal;

  const couponDiscount = computeCouponDiscount(coupon, {
    planId: plan?.plan_id,
    additionalUserPlanId: plan?.additional_user_plan?.id,
    planBase,
    addUsersCost,
    additionalUsers: Math.max(0, additionalUsers),
  });
  const discountedSubTotal = Math.max(0, subTotal - couponDiscount);

  const taxPct = isTaxApplicable ? Number(plan.tax_percent ?? 0) / 100 : 0;
  const tax = Math.round(discountedSubTotal * taxPct * 100) / 100;
  const taxedTotal = discountedSubTotal + tax || 0;
  const feePct = Number(plan.credit_card_fee_percent ?? 0) / 100;
  const fee = Math.round(taxedTotal * feePct * 100) / 100;
  const estimatedTotal = Math.round((taxedTotal + fee) * 100) / 100;

  return {
    planBase,
    addPerSeat,
    addUsersCost,
    subTotal,
    subTotalSavings,
    couponDiscount,
    tax,
    fee,
    estimatedTotal,
    taxedTotal,
  };
};

export const computeCouponDiscount = (
  coupon: AppliedCoupon,
  {
    planId,
    additionalUserPlanId,
    planBase,
    addUsersCost,
    additionalUsers = 0,
  }: {
    planId: any;
    additionalUserPlanId: any;
    planBase: number;
    addUsersCost: number;
    additionalUsers?: number;
  }
) => {
  if (!coupon || !(Number(coupon.discount_value) > 0)) {
    return 0;
  }
  const applicable = (coupon.applicable_product_ids || []).map(String);
  const value = Number(coupon.discount_value);
  const isPercent = coupon.discount_type === "percent";

  // Each applicable product is discounted independently.
  // - The base plan is a single unit (quantity 1).
  // - Additional users are priced per seat, so a flat discount applies per seat.
  // Flat discounts are capped at each product's total base.
  const productItems = [
    { id: planId, base: planBase, quantity: 1 },
    {
      id: additionalUserPlanId,
      base: addUsersCost,
      quantity: Math.max(0, additionalUsers),
    },
  ];

  const discount = productItems.reduce((sum, item) => {
    if (!applicable.includes(String(item.id)) || item.base <= 0) {
      return sum;
    }
    const itemDiscount = isPercent
      ? item.base * (value / 100)
      : Math.min(value * item.quantity, item.base);
    return sum + itemDiscount;
  }, 0);

  return Math.round(discount * 100) / 100;
};

export const computeAdditionalUserPlanPrice = (plan: any) => {
  const basePrice = plan?.base_price ?? 0;
  const discountPercent = plan?.discount_percent ?? 0;
  const discountedPrice = basePrice * (1 - discountPercent / 100);
  return discountedPrice;
};

export const getHtmlSource = (
  planExpired: boolean,
  isMobile: boolean,
  isTablet: boolean,
  expiryDate: string,
  linkUrl: string,
  expiredLinkUrl: string,
  t: any,
  isEnrolledTrial: boolean,
  has7DaysLeft: boolean
) => {
  if (isEnrolledTrial) {
    if (has7DaysLeft) {
      return t(
        "accountProfile.popUp.enrolled.endingSoon.message.text",
        `<div style=font-family: Roboto; font-size: 14px; font-style: regular; font-weight: 400; line-height: 20px;>
<strong>Action Required by {{expiryDate}}</strong>
</div> 
<div style=font-family: Roboto; font-size: 14px; font-style: regular; font-weight: 400; line-height: 20px;>
This is your final countdown. Review your users <strong>before {{expiryDate}}</strong> to make sure the right users remain active.
</div>
<div id="spacer" class="spacer" style="height: 8px;"></div>
<div style=font-family: Roboto; font-size: 14px; font-style: regular; font-weight: 400; line-height: 20px;>
<strong>Why this matters</strong>
</div> 
<div style=font-family: Roboto; font-size: 14px; font-style: regular; font-weight: 400; line-height: 20px;>
After that date, users above your included amount will lose full marketplace access unless additional users are purchased or users are removed.
</div>
<div id="spacer" class="spacer" style="height: 8px;"></div>
<div style=font-family: Roboto; font-size: 14px; font-style: regular; font-weight: 400; line-height: 20px;>
<strong>What do I need to do?</strong>
</div> 
<div style=font-family: Roboto; font-size: 14px; font-style: regular; font-weight: 400; line-height: 20px;>
Go to your Profile to review your active users and make any needed updates.
</div>
<div style=font-family: Roboto; font-size: 14px; font-style: regular; font-weight: 400; line-height: 20px;>
<strong>Need Help?</strong>
</div> 
<div style=font-family: Roboto; font-size: 14px; font-style: regular; font-weight: 400; line-height: 20px;>
Questions? Contact <a href="mailto:support@vdbapp.com" target="_blank" style="color: #007AFF; text-decoration: none;">support@vdbapp.com</a>
</div>
`,
        { expiryDate: expiryDate }
      );
    } else {
      return t(
        "accountProfile.popUp.enrolled.message.text",
        `<div style=font-family: Roboto; font-size: 14px; font-style: regular; font-weight: 400; line-height: 20px;>
<strong>Action Required by {{expiryDate}}</strong>
</div> 
<div style=font-family: Roboto; font-size: 14px; font-style: regular; font-weight: 400; line-height: 20px;>
We're applying marketplace user limits to supplier accounts. Review your users <strong>before {{expiryDate}}</strong> to make sure the right users remain active.
</div>
<div id="spacer" class="spacer" style="height: 8px;"></div>
<div style=font-family: Roboto; font-size: 14px; font-style: regular; font-weight: 400; line-height: 20px;>
<strong>Why this matters</strong>
</div> 
<div style=font-family: Roboto; font-size: 14px; font-style: regular; font-weight: 400; line-height: 20px;>
After that date, users above your included amount will lose full marketplace access unless additional users are purchased or users are removed.
</div>
<div id="spacer" class="spacer" style="height: 8px;"></div>
<div style=font-family: Roboto; font-size: 14px; font-style: regular; font-weight: 400; line-height: 20px;>
<strong>What do I need to do?</strong>
</div> 
<div style=font-family: Roboto; font-size: 14px; font-style: regular; font-weight: 400; line-height: 20px;>
Go to your Profile to review your active users and make any needed updates.
</div>
<div style=font-family: Roboto; font-size: 14px; font-style: regular; font-weight: 400; line-height: 20px;>
<strong>Need Help?</strong>
</div> 
<div style=font-family: Roboto; font-size: 14px; font-style: regular; font-weight: 400; line-height: 20px;>
Questions? Contact <a href="mailto:support@vdbapp.com" target="_blank" style="color: #007AFF; text-decoration: none;">support@vdbapp.com</a>
</div>
`,
        { expiryDate: expiryDate }
      );
    }
  }
  if (planExpired) {
    if (isMobile || isTablet) {
      return t(
        "accountProfile.popUp.expired.mobile.message.text",
        `
<div style=font-family: Roboto; font-size: 14px; font-style: regular; font-weight: 400; line-height: 20px;>
<strong>Your account is now in <i>Limited View Mode</i>. Some features may be restricted.</strong>
</div>  
<div id="spacer" class="spacer" style="height: 8px;"></div> 
<div style="font-family: Roboto; font-size: 14px; font-style: regular; font-weight: 400; line-height: 20px;">
To regain full Marketplace access, please visit your Profile section from any web browser.</div>
<div id="spacer" class="spacer" style="height: 8px;"></div> 
<div style="font-family: Roboto; font-size: 14px; font-style: regular; font-weight: 400; line-height: 20px;">   
VDB now uses an enhanced membership structure with new access levels.</div> 
 <div id="spacer" class="spacer" style="height: 8px;"></div>      
<div style="font-family: Roboto; font-size: 14px; font-style: regular; font-weight: 400; line-height: 20px;">   
As a reminder, <strong>new members can apply their membership payment toward any VDB Solution within 90 days.</strong> <a href={{linkUrl}} target="_blank" style="color: #007AFF; text-decoration: none;">Learn more</a></div>
<div id="spacer" class="spacer" style="height: 8px;"></div>
<div style="font-family: Roboto; font-size: 14px; font-style: regular; font-weight: 400; line-height: 20px;">   
If you are a <strong>listing supplier</strong>, an <strong>active VDB Solutions client</strong>, or <strong>have already paid for your membership</strong> and are seeing this message, please contact <a href="mailto:support@vdbapp.com" target="_blank" style="color: #007AFF; text-decoration: none;">support@vdbapp.com</a>.</div>
`,
        { expiryDate: expiryDate, linkUrl: expiredLinkUrl }
      );
    } else {
      return t(
        "accountProfile.popUp.expired.message.text",
        `
 <div style=font-family: Roboto; font-size: 14px; font-style: regular; font-weight: 400; line-height: 20px;>
Your trial period has ended, and your account is now in <i>Limited View Mode</i>.
</div>  
<div id="spacer" class="spacer" style="height: 8px;"></div> 
 <div style="font-family: Roboto; font-size: 14px; font-style: regular; font-weight: 400; line-height: 20px;">
To restore full access to the VDB Marketplace and all its features, please activate your membership from the <strong>Profile</strong> section of your account.</div>
<div id="spacer" class="spacer" style="height: 8px;"></div> 
<div style="font-family: Roboto; font-size: 14px; font-style: regular; font-weight: 400; line-height: 20px;">   
 If you missed our recent announcements, VDB now operates under a new membership plan. <a href=https://explore.vdbapp.com/membership?utm_source=advertisements_newsletters&utm_medium=marketplace&utm_campaign=membership target="_blank" style="color: #007AFF; text-decoration: none;">See details</a></div> 
   <div id="spacer" class="spacer" style="height: 8px;"></div>      
<div style="font-family: Roboto; font-size: 14px; font-style: regular; font-weight: 400; line-height: 20px;">   
As a reminder, <strong>new members can apply their membership payment toward any VDB Solution within 90 days.</strong> <a href={{linkUrl}} target="_blank" style="color: #007AFF; text-decoration: none;">Learn more</a></div>
 <div id="spacer" class="spacer" style="height: 8px;"></div>
<div style="font-family: Roboto; font-size: 14px; font-style: regular; font-weight: 400; line-height: 20px;">   
 If you are a <strong>listing supplier</strong>, an <strong>active VDB Solutions client</strong>, or <strong>have already paid for your membership</strong> and are seeing this message, please contact <a href="mailto:support@vdbapp.com" target="_blank" style="color: #007AFF; text-decoration: none;">support@vdbapp.com</a>.</div>
 `,
        { expiryDate: expiryDate, linkUrl: expiredLinkUrl }
      );
    }
  } else {
    if (isMobile || isTablet) {
      return t(
        "accountProfile.popUp.mobile.message.text",
        `
<div style=font-family: Roboto; font-size: 14px; font-style: regular; font-weight: 400; line-height: 20px;>
A new <strong>Profile section</strong> is now available to manage your account, membership, and users.
</div>  
<div id="spacer" class="spacer" style="height: 8px;"></div> 
<div style="font-family: Roboto; font-size: 14px; font-style: regular; font-weight: 400; line-height: 20px;">
<strong>Set up your membership by {{expiryDate}}</strong> to keep full Marketplace access.</div>
<div id="spacer" class="spacer" style="height: 8px;"></div> 
<div style="font-family: Roboto; font-size: 14px; font-style: regular; font-weight: 400; line-height: 20px;">   
Listing suppliers and active solutions clients don't need to take action.</div> 
 <div id="spacer" class="spacer" style="height: 8px;"></div>    
<div style="font-family: Roboto; font-size: 14px; font-style: regular; font-weight: 400; line-height: 20px;">   
New members can apply their payment toward any <strong>VDB Solution</strong> within 90 days. <a href={{linkUrl}} target="_blank" style="color: #007AFF; text-decoration: none;">Learn more</a></div>
`,
        { expiryDate: expiryDate, linkUrl: linkUrl }
      );
    } else {
      return t(
        "accountProfile.popUp.message.text",
        `
<div style=font-family: Roboto; font-size: 14px; font-style: regular; font-weight: 400; line-height: 20px;>
A new <strong>Profile section</strong> is now available to manage your account, membership, and users.
</div>  
<div id="spacer" class="spacer" style="height: 8px;"></div> 
<div style="font-family: Roboto; font-size: 14px; font-style: regular; font-weight: 400; line-height: 20px;">
<strong>Set up your membership by {{expiryDate}}</strong> to keep full Marketplace access.</div>
<div id="spacer" class="spacer" style="height: 8px;"></div> 
<div style="font-family: Roboto; font-size: 14px; font-style: regular; font-weight: 400; line-height: 20px;">   
Listing suppliers and active solutions clients don't need to take action.</div> 
 <div id="spacer" class="spacer" style="height: 8px;"></div>    
<div style="font-family: Roboto; font-size: 14px; font-style: regular; font-weight: 400; line-height: 20px;">   
New members can apply their payment toward any <strong>VDB Solution</strong> within 90 days. <a href={{linkUrl}} target="_blank" style="color: #007AFF; text-decoration: none;">Learn more</a></div>
`,
        { expiryDate: expiryDate, linkUrl: linkUrl }
      );
    }
  }
};

export const formatToday = () => {
  const date = new Date();
  return date
    .toLocaleDateString("en-GB", {
      timeZone: "UTC",
      day: "2-digit",
      month: "short",
      year: "numeric",
    })
    .toLowerCase()
    .replace(/ /g, "-");
};

export const formatDate = (dateString: string) => {
  const newDate = new Date(dateString).toLocaleDateString();
  const [day, month, year] = newDate.split("/");
  const date = new Date(year, month - 1, day);
  return date.toLocaleDateString("en-US", {
    month: "short", // "Nov"
    day: "2-digit", // "22"
    year: "numeric", // "2026"
  });
};

export const computePurchaseSeatsPrice = (
  selectedPlan: any,
  additionalUsers: number,
  userInfo: any
) => {
  const planBase = (selectedPlan?.plan_info || []).reduce(
    (sum: number, p: any) => {
      const base = p.base_price ?? 0;
      const discount = p.discount_percent ? Number(p.discount_percent) : 0;
      const discounted = base * (1 - discount / 100);
      return sum + discounted;
    },
    0
  );

  const addBase = selectedPlan?.additional_user_plan?.base_price ?? 0;
  const addDiscountedPrice =
    selectedPlan?.additional_user_plan?.discounted_price ?? 0;
  const addDiscount = selectedPlan?.additional_user_plan?.discount_percent
    ? Number(selectedPlan?.additional_user_plan?.discount_percent)
    : 0;
  const addPerSeat =
    addDiscountedPrice !== 0
      ? addDiscountedPrice
      : addBase * (1 - addDiscount / 100);
  const additionalUsersPrice =
    addPerSeat * (selectedPlan?.plan_type ?? 1) * Math.max(0, additionalUsers);

  const tax = userInfo?.user?.is_tax_applicable
    ? (additionalUsersPrice * (selectedPlan?.tax_percent ?? 0)) / 100
    : 0;
  const taxedTotal = ((additionalUsersPrice + tax) * 100) / 100;
  const fee = (taxedTotal * (selectedPlan?.credit_card_fee_percent ?? 0)) / 100;
  const estimatedTotal = ((additionalUsersPrice + tax + fee) * 100) / 100;
  return {
    additionalUsersPrice,
    tax,
    fee,
    estimatedTotal,
    taxedTotal,
    planBase,
  };
};
